import React from "react";
import { Context } from "@hotwired/stimulus";
import { ReactWrapperController } from "@root/server/client/esweb/src/lib/stimulus/ReactWrapperController.ts";
import { Button } from "@root/server/client/esweb/components/ui/button.tsx";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@root/server/client/esweb/lib/utils.ts";

export const name = "ui--copy-to-clipboard";

export default class extends ReactWrapperController {
    constructor(context: Context) {
        super(context, (props) => <CopyToClipboardButton {...props} />);
    }
}

export interface CopyToClipboardProps {
    text: string;
    variant?: "outline" | "ghost" | "default";
    size?: "icon" | "sm" | "default";
    children?: React.ReactNode;
    className?: string;
    iconClassName?: string;
}

export function CopyToClipboardButton({ text, variant = "outline", size = "icon", children, className, iconClassName }: CopyToClipboardProps) {
    const copyToClipboard = () => {
        if (!text) return;

        if (navigator.clipboard && navigator.clipboard.writeText) {
            navigator.clipboard.writeText(text).then(() => {
                toast.success("Copied to clipboard");
            }).catch(() => {
                fallbackCopy(text);
            });
        } else {
            fallbackCopy(text);
        }
    };

    const fallbackCopy = (t: string) => {
        const textArea = document.createElement("textarea");
        textArea.value = t;
        textArea.style.position = "fixed";
        textArea.style.left = "-9999px";
        textArea.style.top = "0";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
            const successful = document.execCommand('copy');
            if (successful) {
                toast.success("Copied to clipboard");
            } else {
                toast.error("Failed to copy");
            }
        } catch (err) {
            toast.error("Failed to copy manually");
        }

        document.body.removeChild(textArea);
    };

    return (
        <Button type="button" variant={variant} size={size} onClick={copyToClipboard} className={className}>
            {children || <Copy className={cn("h-4 w-4", iconClassName)} />}
        </Button>
    );
}
